import React, { useState, useEffect } from 'react';
import { WebhookSubscription } from '../types';
import { apiService } from '../services/api';

export const WebhookDashboard: React.FC = () => {
  const [webhooks, setWebhooks] = useState<WebhookSubscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [setupLoading, setSetupLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [webhookUrl, setWebhookUrl] = useState('');

  // Load webhook subscriptions
  const loadWebhooks = async () => {
    try {
      setLoading(true);
      const response = await apiService.listWebhooks();
      setWebhooks(response.webhooks);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  };

  // Setup webhooks
  const handleSetupWebhooks = async () => {
    if (!webhookUrl.trim()) {
      setError('Please enter a webhook URL');
      return;
    }

    try {
      setSetupLoading(true);
      await apiService.setupWebhooks(webhookUrl.trim());
      await loadWebhooks(); // Refresh the list
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to setup webhooks');
    } finally {
      setSetupLoading(false);
    }
  };

  // Remove webhooks
  const handleRemoveWebhooks = async (url: string) => {
    try {
      await apiService.removeWebhooks(url);
      await loadWebhooks(); // Refresh the list
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove webhooks');
    }
  };

  // Load webhooks on mount
  useEffect(() => {
    loadWebhooks();
  }, []);

  // Get unique URLs for grouping
  const uniqueUrls = Array.from(new Set(webhooks.map(w => w.Url)));

  // Group webhooks by URL
  const webhooksByUrl = uniqueUrls.map(url => ({
    url,
    webhooks: webhooks.filter(w => w.Url === url),
    totalCount: webhooks.filter(w => w.Url === url).length
  }));

  // Get default webhook URL suggestion
  const defaultWebhookUrl = window.location.origin;

  return (
    <div className="webhook-dashboard">
      <div className="webhook-setup-section">
        <h2>Setup Webhooks</h2>
        <p className="section-description">
          Configure Brushfire webhooks to enable real-time data synchronization.
          Each event type will be automatically configured with its own endpoint.
        </p>

        <div className="webhook-setup-form">
          <div className="form-group">
            <label htmlFor="webhookUrl">Base Webhook URL:</label>
            <input
              id="webhookUrl"
              type="url"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder={`${defaultWebhookUrl}/webhooks`}
              className="webhook-url-input"
            />
            <small className="form-help">
              Enter your base webhook URL. Individual endpoints will be created automatically for each event type.
            </small>
          </div>

          <button
            onClick={handleSetupWebhooks}
            disabled={setupLoading || !webhookUrl.trim()}
            className="setup-button"
          >
            {setupLoading ? '⏳ Setting up...' : '🔗 Setup Webhooks'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span>❌ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="webhook-status-section">
        <div className="section-header">
          <h2>Webhook Status</h2>
          <button
            onClick={loadWebhooks}
            disabled={loading}
            className="refresh-button"
          >
            🔄 Refresh
          </button>
        </div>

        {loading ? (
          <div className="loading">Loading webhooks...</div>
        ) : webhooks.length === 0 ? (
          <div className="no-webhooks">
            <div className="empty-state">
              <h3>No webhooks configured</h3>
              <p>Set up webhooks above to enable real-time synchronization from Brushfire.</p>
            </div>
          </div>
        ) : (
          <div className="webhook-groups">
            {webhooksByUrl.map(group => (
              <div key={group.url} className="webhook-group">
                <div className="webhook-group-header">
                  <div className="webhook-url">
                    <h3>{group.url}</h3>
                    <span className="webhook-count">
                      {group.totalCount} Webhooks
                    </span>
                  </div>
                  <button
                    onClick={() => handleRemoveWebhooks(group.url)}
                    className="remove-button"
                    title="Remove all webhooks for this URL"
                  >
                    🗑️ Remove
                  </button>
                </div>

                <div className="webhook-list">
                  {group.webhooks.map(webhook => (
                    <div key={webhook.Id} className="webhook-item active">
                      <div className="webhook-info">
                        <div className="webhook-type">
                          <span className="event-type">{webhook.Type}</span>
                          <span className="status-badge active">
                            ✅ Active
                          </span>
                        </div>
                        <div className="webhook-details">
                          <small>ID: {webhook.Id}</small>
                          {webhook.Objects && webhook.Objects.length > 0 && (
                            <small>Objects: {webhook.Objects.join(', ')}</small>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="webhook-info-section">
        <h3>Supported Event Types</h3>
        <div className="event-types-grid">
          <div className="event-type-category">
            <h4>Events</h4>
            <ul>
              <li>Event_Created</li>
              <li>Event_Updated</li>
            </ul>
          </div>
          <div className="event-type-category">
            <h4>Groups</h4>
            <ul>
              <li>Group_Created</li>
              <li>Group_Updated</li>
              <li>Group_Deleted</li>
            </ul>
          </div>
          <div className="event-type-category">
            <h4>Orders</h4>
            <ul>
              <li>Order_Updated</li>
              <li>Order_Completed</li>
            </ul>
          </div>
          <div className="event-type-category">
            <h4>Attendees</h4>
            <ul>
              <li>Attendee_Created</li>
              <li>Attendee_Updated</li>
              <li>Attendee_Cancelled</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
